import { api, withIdempotencyKey, type Page } from './client'

export interface Customer {
  id: number
  customerCode: string
  customerType: string
  firstName?: string
  lastName?: string
  companyName?: string
  email?: string
  phone?: string
  city?: string
  province?: string
  status: string
  createdAt: string
  /** Dall'elenco (27/10/2026): bastano a precompilare una pratica anche quando la scheda completa non è leggibile. */
  mobile?: string
  address?: string
  postalCode?: string
  fiscalCode?: string
  vatNumber?: string
  pec?: string
}

export interface CustomerDetail extends Customer {
  mobile?: string
  address?: string
  postalCode?: string
  country?: string
  fiscalCode?: string
  vatNumber?: string
  pec?: string
  sdiCode?: string
  notes?: string
  birthDate?: string | null
  birthCity?: string | null
  birthProvince?: string | null
}

/**
 * Body of `POST /api/customers` and `PUT /api/customers/{id}` (TASK-0046, widened to the full
 * 6-value REQ-004-002 vocabulary by TASK-0165). `customerType` must be the domain vocabulary —
 * `CustomerFormPage.tsx` owns the mapping from its own Italian form vocabulary, this type does not
 * accept the Italian values. `forceCreate` only affects `create` (the backend's duplicate check
 * only runs on create, same as `/api/companies` and `/api/people` — see
 * `CustomersController.Create`); it is accepted here too since both calls share this request
 * shape, but `update` never reads it (nor does it read `customerType` at all — see
 * `CustomersController.Update`/`Customer.Update`).
 */
export interface CustomerCreateRequest {
  customerType: 'person' | 'sole_proprietor' | 'professional' | 'company' | 'condominium' | 'other'
  firstName?: string | undefined
  lastName?: string | undefined
  companyName?: string | undefined
  email?: string | undefined
  phone?: string | undefined
  mobile?: string | undefined
  address?: string | undefined
  city?: string | undefined
  postalCode?: string | undefined
  province?: string | undefined
  country?: string | undefined
  fiscalCode?: string | undefined
  vatNumber?: string | undefined
  pec?: string | undefined
  sdiCode?: string | undefined
  notes?: string | undefined
  forceCreate?: boolean | undefined
  /** Solo persona fisica / ditta / professionista (yyyy-MM-dd). */
  birthDate?: string | null | undefined
  birthCity?: string | null | undefined
  birthProvince?: string | null | undefined
}

export const LEGAL_REP_ROLES = ['Legale rappresentante', 'Amministratore', 'Titolare'] as const

/** Rappresentante legale di societa' / condominio (uno per cliente). */
export interface LegalRepresentative {
  firstName?: string | null
  lastName?: string | null
  fiscalCode?: string | null
  birthDate?: string | null
  birthCity?: string | null
  birthProvince?: string | null
  residenceStreet?: string | null
  residenceCivic?: string | null
  residenceZip?: string | null
  residenceCity?: string | null
  residenceProvince?: string | null
  role?: string | null
}


export const customersApi = {
  /** `sort` (F-095): created_desc (default) | created_asc | name_asc | name_desc | code_asc | code_desc. */
  list: (p: { search?: string; page?: number; size?: number; sort?: string } = {}) => {
    const q = new URLSearchParams()
    if (p.search) q.set('search', p.search)
    q.set('page', String(p.page ?? 1))
    q.set('pageSize', String(p.size ?? 25))
    if (p.sort) q.set('sort', p.sort)
    return api<Page<Customer>>(`/api/customers?${q}`)
  },
  get: (id: number) => api<CustomerDetail>(`/api/customers/${id}`),
  create: (data: CustomerCreateRequest, idempotencyKey?: string) =>
    api<{ id: number }>(
      '/api/customers',
      withIdempotencyKey({ method: 'POST', body: JSON.stringify(data) }, idempotencyKey),
    ),
  update: (id: number, data: CustomerCreateRequest) =>
    api<void>(`/api/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getLegalRepresentative: (id: number) =>
    api<{ representative: LegalRepresentative | null }>(`/api/customers/${id}/legal-representative`),
  putLegalRepresentative: (id: number, data: LegalRepresentative) =>
    api<unknown>(`/api/customers/${id}/legal-representative`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLegalRepresentative: (id: number) =>
    api<void>(`/api/customers/${id}/legal-representative`, { method: 'DELETE' }),
}

/**
 * TASK-0045: `SupplyPointsController` (module Customers) — POD/PDR energy connections and
 * phone/fiber lines. Backend CRUD was already complete and authenticated; this is the first
 * frontend consumer. `SupplyPointRequest` was extended server-side in the same task to carry the
 * energy/telephony fields below (previously only SupplyType/CustomerId/ContractId/Code/City/
 * Province/Notes were exposed) — see the record's own doc comment in `SupplyPointsController.cs`
 * for which domain fields were deliberately left out (`Zip`/`ContactId`) and why.
 */
export interface SupplyPoint {
  id: number
  supplyType: string // pod | pdr | phone | fiber
  code?: string
  customerId?: number
  contractId?: number
  address?: string
  city?: string
  province?: string
  isResident: boolean
  powerKw?: number
  annualConsumption?: number
  meterType: string
  meterSerial?: string
  usageType: string
  isVulnerable: boolean
  lineType?: string
  isMigration: boolean
  migrationCode?: string
  notes?: string
  createdAt: string
}

export interface SupplyPointRequest {
  supplyType: string
  customerId?: number | undefined
  contractId?: number | undefined
  code?: string | undefined
  city?: string | undefined
  province?: string | undefined
  notes?: string | undefined
  address?: string | undefined
  isResident?: boolean | undefined
  powerKw?: number | undefined
  annualConsumption?: number | undefined
  meterType?: string | undefined
  meterSerial?: string | undefined
  usageType?: string | undefined
  isVulnerable?: boolean | undefined
  lineType?: string | undefined
  isMigration?: boolean | undefined
  migrationCode?: string | undefined
}

export const supplyPointsApi = {
  list: (customerId: number) =>
    api<Page<SupplyPoint>>(`/api/supply-points?customerId=${customerId}&pageSize=100`),
  create: (data: SupplyPointRequest) =>
    api<{ id: number }>('/api/supply-points', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: SupplyPointRequest) =>
    api<void>(`/api/supply-points/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: number) => api<void>(`/api/supply-points/${id}`, { method: 'DELETE' }),
}

/**
 * F-111 (REQ-006-009, REQ-042-003): tipi di punto di fornitura e relativi campi di settore, decisi dall'Admin
 * (`/api/supply-point-types`) invece che scritti nelle schermate. Senza nessun tipo configurato il server
 * restituisce i quattro storici (`isFallback: true`).
 */
export interface SupplyPointFieldOption {
  value: string
  label: string
}

export interface SupplyPointFieldDef {
  key: string
  label: string
  kind: 'decimal' | 'integer' | 'select' | 'boolean' | 'text'
  options?: SupplyPointFieldOption[] | null
}

export interface SupplyPointType {
  /** `null` per i tipi storici di ripiego, che non hanno ancora una riga. */
  id: number | null
  code: string
  label: string
  group: 'energia' | 'telefonia' | 'altro'
  sortOrder: number
  isActive: boolean
  isFallback: boolean
  fieldKeys: string[]
  fields: SupplyPointFieldDef[]
}

export interface SupplyPointTypeRequest {
  code: string
  label: string
  group: string
  fields: string[]
  sortOrder?: number | undefined
  isActive?: boolean | undefined
}

export const supplyPointTypesApi = {
  /** `includeInactive` vale solo per l'Admin; per gli altri il server lo ignora. */
  list: (includeInactive = false) =>
    api<{ total: number; items: SupplyPointType[] }>(
      `/api/supply-point-types${includeInactive ? '?includeInactive=true' : ''}`,
    ),
  /** Catalogo chiuso dei campi che un tipo puo' portare. */
  fieldCatalog: () =>
    api<{ groups: string[]; items: SupplyPointFieldDef[] }>('/api/supply-point-types/fields'),
  create: (data: SupplyPointTypeRequest) =>
    api<{ id: number; code: string }>('/api/supply-point-types', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: SupplyPointTypeRequest) =>
    api<void>(`/api/supply-point-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setActive: (id: number, isActive: boolean) =>
    api<void>(`/api/supply-point-types/${id}/active`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
}
